// eslint-disable-next-line import/no-unresolved
import brand from '@brand/brand.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as z from 'zod';
import { IS_PRODUCTION } from '../../../../shared/constants';
import { useRequestOtp } from '../../api/use-request-otp';
import { BrandedLayout } from '../../components/layouts/branded-layout';
import { SubmitButton } from '../../components/submit-button';
import { DateField } from '../../components/ui/date-field';
import { ErrorBlock } from '../../components/ui/error-block';
import { InfoBlock } from '../../components/ui/info-block';
import { Input } from '../../components/ui/input';
import { useInviteStore } from '../../store/use-invite-store';
import { API_DATE_REGEX } from '../../utils/constants';
import { cleanPhone } from './../../utils/phone';

const resolver = zodResolver(
  z.object({
    name: z.string().min(1),
    dateOfBirth: z.string().regex(API_DATE_REGEX),
  }),
);

let defaultValues = { name: '', dateOfBirth: '' };
if (!IS_PRODUCTION) {
  defaultValues.name = localStorage.getItem('soopa-invite-code-name') ?? '';
  defaultValues.dateOfBirth = localStorage.getItem('soopa-invite-code-dob') ?? '';
}

export function Welcome() {
  const { invite } = useInviteStore();
  const navigate = useNavigate();
  const { t } = useTranslation(['page.welcome', 'common']);
  const requestOtp = useRequestOtp();
  const form = useForm({ defaultValues, resolver, mode: 'onChange' });

  const onSubmit = useCallback(
    async ({ name, dateOfBirth }) => {
      try {
        await requestOtp.execute(name, dateOfBirth);
        navigate('../code');
      } catch {
        // Handled
      }
    },
    [navigate, requestOtp],
  );

  return (
    <BrandedLayout>
      <div className="mb-5">
        <h1 className="title font-medium">
          <Trans
            i18nKey="title"
            t={t}
            components={{ strong: <strong className="block" /> }}
            values={{ company: brand.company }}
          />
        </h1>
        <Trans
          i18nKey="text"
          t={t}
          components={{
            p: <p />,
            strong: <strong className="font-semibold" />,
            PhonePlaceholder: <span>{invite?.phone}</span>,
          }}
        />
      </div>

      <ErrorBlock error={requestOtp.error} />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="pt-2.5 space-y-4">
          <Input
            name="name"
            label={t('familyName')}
            autoFocus
            className="uppercase"
            autoComplete="family-name"
          />
          <DateField name="dateOfBirth" label={t('birthDate')} autoComplete="bday" />
          <SubmitButton label={t('sendCode')} />
        </form>
      </FormProvider>

      <InfoBlock>
        <Trans
          i18nKey="helpMessage"
          t={t}
          values={{ phone: brand.phone }}
          components={{
            p: <p />,
            br: <br />,
            strong: <strong className="font-semibold" />,
            a: <a href={`tel:${cleanPhone(brand.phone)}`} />,
          }}
        />
      </InfoBlock>
    </BrandedLayout>
  );
}
